import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import { fetchReport, handleError, formatDateForMySQL, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import ListItemSupplyEdit from '../../layouts/ListItemSupplyEdit';
import dayjs from 'dayjs';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const SupplyReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [supplyDialog, setSupplyDialog] = useState({ open: false, id: null });
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());

  // Función para obtener el primer propietario de una mascota
  const getFirstOwner = (pet) => {
    return pet?.owners && pet.owners.length > 0 ? pet.owners[0] : null;
  };
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
    }

    fetchSupplies();
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page,
    });
  };

  const fetchSupplies = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SUPPLY', page, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
 
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchSupplies();
  };

  const handleMarkSuppliedClick = (supply) => {
    setSupplyDialog({ open: true, id: supply.id });
  };

  const handleMarkSuppliedConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.put(
        `${import.meta.env.VITE_API_URL}pets_supply?id=${supplyDialog.id}&check`,
        {},
        { withCredentials: true }
      );
      
      // Refresh the report data
      fetchSupplies();
      snackbar(t('reports.supplyCompleted'));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      setSupplyDialog({ open: false, id: null });
    }
  };

  const handleDeleteClick = (supply) => {
    setDeleteDialog({ open: true, id: supply.id });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.delete(
        `${import.meta.env.VITE_API_URL}pets_supply?id=${deleteDialog.id}`,
        { withCredentials: true }
      );
      
      // Refresh the report data
      fetchSupplies();
      snackbar(t('reports.supplyDeleted'));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleCloseDialogs = () => {
    setDeleteDialog({ open: false, id: null });
    setSupplyDialog({ open: false, id: null });
  };

  const handleRowClick = (supply) => {
    // Save current state before navigating
    saveCurrentState();
    // Navigate to pet profile
    navigate('/main/view-pet', { state: { id: supply.pet.id, updateLastView: false } });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <SearchIcon 
                onClick={fetchSupplies}
                sx={{ 
                  fontSize: 28, 
                  cursor: 'pointer',
                  color: isLoading ? 'text.disabled' : 'text.primary',
                  display: 'block',
                  margin: 'auto'
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Typography variant="h6">
                  {t('reports.supplyTitle')} ({data.total_results || data.content.length})
                </Typography>
              </Box>
              
              <Container maxWidth="xl" sx={{ mt: 2, px: 0 }}>
                {data.content.map((supply, index) => {
                  const firstOwner = getFirstOwner(supply.pet);
                  return (
                    <Box key={supply.id}>
                      <ListItemSupplyEdit
                        product={supply.product?.name || t('reports.productUnavailable')}
                        description={`${formatDate2(supply.date_tentative)} ${supply.date_supply ? t('reports.supplied') : t('reports.notSupplied')} - ${
                          firstOwner
                            ? t('reports.petOfOwnerLabel', { pet: supply.pet.name, owner: firstOwner.name })
                            : supply.pet.name
                        }`}
                        thumbUrl={supply.product?.thumb_url}
                        avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
                        onMarkSupplied={!supply.date_supply ? () => handleMarkSuppliedClick(supply) : undefined}
                        onDelete={() => handleDeleteClick(supply)}
                        onClick={() => handleRowClick(supply)}
                        dateSupply={supply.date_supply}
                        dateTentative={supply.date_tentative}
                        index={index}
                      />
                    </Box>
                  );
                })}
              </Container>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noSupplies')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateRange')}</Typography>
        </Box>
      )}

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDialogs}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('reports.deleteSupplyConfirm')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reports.deleteSupplyIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para marcar como suministrado */}
      <Dialog
        open={supplyDialog.open}
        onClose={handleCloseDialogs}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('reports.confirmSupply')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reports.confirmSupplyDesc')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleMarkSuppliedConfirm} color="primary" autoFocus>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplyReport; 